import { useState } from "react";
import axios from "axios";
import { ArrowRight, User, KeyRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "femora_access";

const AccessGate = ({ children }) => {
  const [entered, setEntered] = useState(() => sessionStorage.getItem(SESSION_KEY) === "1");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Please tell us your name.");
      return;
    }
    if (!code.trim()) {
      setError("Please enter the access code.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/access/enter`, { name: name.trim(), code: code.trim() });
      sessionStorage.setItem(SESSION_KEY, "1");
      setEntered(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (entered) return children;

  return (
    <main
      data-testid="access-gate"
      className="min-h-screen flex items-center justify-center px-5"
      style={{ background: "linear-gradient(160deg,#FBF1F3 0%,#FDFBF7 45%,#F3EEF8 100%)" }}
    >
      <div className="w-full max-w-md">
        <div className="bg-white border border-gray-200 rounded-2xl p-7 md:p-9 shadow-[0_20px_60px_-30px_rgba(10,17,40,0.3)]">
          <p className="text-xs font-semibold uppercase tracking-widest text-rose mb-2">
            Private preview
          </p>
          <h1 className="font-display text-4xl font-medium tracking-tight text-ink">
            Welcome to Femora Life
          </h1>
          <p className="text-sm text-gray-500 mt-3">
            We're opening our doors to a small circle first. Please tell us your name and enter
            the access code you were given.
          </p>

          <form onSubmit={submit} data-testid="access-gate-form" className="mt-8 space-y-4">
            <div className="relative">
              <User size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="access-name-input"
                placeholder="Your name"
                autoComplete="name"
                className="w-full rounded-md border border-gray-300 pl-11 pr-4 py-3 text-ink placeholder:text-gray-400 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10 transition-colors duration-200"
              />
            </div>
            <div className="relative">
              <KeyRound size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                data-testid="access-code-input"
                placeholder="Access code"
                autoComplete="off"
                className="w-full rounded-md border border-gray-300 pl-11 pr-4 py-3 text-ink placeholder:text-gray-400 outline-none focus:border-ink focus:ring-2 focus:ring-ink/10 transition-colors duration-200"
              />
            </div>
            {error && (
              <p data-testid="access-gate-error" className="text-sm text-red-600">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="access-gate-submit"
              className="w-full flex items-center justify-center gap-2 rounded-md bg-ink text-white font-semibold py-3.5 hover:bg-rose transition-colors duration-200 disabled:opacity-50"
            >
              {loading ? "Checking…" : "Enter"} <ArrowRight size={16} />
            </button>
            <p className="text-xs text-gray-400 text-center">
              Your name is noted so we know who's had a first look. No account needed.
            </p>
          </form>
        </div>
        <p className="text-center text-xs text-gray-400 mt-5">
          Femora Life — launching globally soon.
        </p>
      </div>
    </main>
  );
};

export default AccessGate;
